import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Drawer,
  Grid,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';

// Utils
import messages from '../../static/messages';

// Services
import paymentMethodsServices from '../../services/paymentMethodsServices';

// Styles
import { button } from '../../static/theme-styles';

const required_fields = ['name'];

const PaymentMethodDrawer = (props) => {
  const { currentItem, setCurrentItem, isNewItem, onCloseDrawer, onItemSaved } = props;
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const resetErrors = () => {
    setErrors({});
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      }))
    );
  };

  const handleClose = () => onCloseDrawer(resetErrors);

  const handleChange = (e) => {
    const key = e.target.name;
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    setCurrentItem((prevState) => ({
      ...prevState,
      [key]: val,
      isEdited: true,
    }));
  };

  const handleValidate = () => {
    let hasErrors = false;
    const newErrors = {};

    required_fields.forEach((field) => {
      if (!currentItem[field] || currentItem[field] === '') {
        newErrors[field] = true;
        hasErrors = true;
      } else {
        newErrors[field] = false;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!handleValidate()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        name: currentItem.name,
        description: currentItem.description || '',
        isActive: currentItem.isActive ?? true,
        orderIndex: parseInt(currentItem.orderIndex) || 0,
      };

      if (isNewItem) {
        await paymentMethodsServices.create(dataToSave);
        toast.success(messages.success.data_has_been_created);
      } else {
        await paymentMethodsServices.update(currentItem.id, dataToSave);
        toast.success(messages.success.data_has_been_updated);
      }

      onItemSaved();
      handleClose();
    } catch (error) {
      toast.error(messages.errors.error_data_update);
      console.log(error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    resetErrors();
  }, [currentItem?.id]);

  return (
    <Drawer anchor="right" open={props.drawer} onClose={handleClose}>
      <Box sx={{ width: 400, p: 3 }}>
        {currentItem && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#0d3c61' }}>
                {isNewItem ? 'Add Payment Method' : 'Edit Payment Method'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                name="name"
                label="Name"
                value={currentItem.name || ''}
                fullWidth
                onChange={handleChange}
                error={errors.name}
                helperText={errors.name && messages.errors.required_field}
                placeholder="e.g. Credit Card"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={currentItem.description || ''}
                fullWidth
                multiline
                rows={2}
                onChange={handleChange}
                placeholder="Optional description"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="orderIndex"
                label="Order"
                type="number"
                value={currentItem.orderIndex || 0}
                fullWidth
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={currentItem.isActive ?? true}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={button}
                  fullWidth
                >
                  {saving ? 'Saving...' : isNewItem ? 'Create' : 'Update'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={saving}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default PaymentMethodDrawer;
